/**
 * Marketing > Campaigns API.
 *
 * Email and SMS broadcasts sent through ActiveCampaign. Every call goes to the
 * portal's own server rather than to ActiveCampaign: their API token is
 * account-wide and would be readable by anyone with the bundle if it were used
 * from the browser.
 */

import { API_BASE } from '../config/apiBase';

export type CampaignChannel = 'email' | 'sms';

export type CampaignStatus =
  | 'draft'
  | 'sending'
  | 'scheduled'
  | 'sent'
  | 'paused'
  | 'stopped'
  | 'failed';

/** The five places a reachable person can come from. */
export type AudienceSource =
  | 'client'
  | 'website_form_lead'
  | 'business_card_scan'
  | 'event_intake_response'
  | 'robotics_proposal';

export const AUDIENCE_SOURCE_LABELS: Record<AudienceSource, string> = {
  client: 'Clients & leads',
  website_form_lead: 'Website form leads',
  business_card_scan: 'Business card scans',
  event_intake_response: 'Event intake responses',
  robotics_proposal: 'Robotics proposals',
};

/**
 * Where a campaign's recipients come from.
 *
 * 'ac_list' sends to a list maintained inside ActiveCampaign. Subscribers stay
 * where they opted in, unsubscribes are already honoured against that list, and
 * the portal reads it rather than owning it — so nobody is pushed into
 * marketing who did not ask to be.
 *
 * 'portal' builds the audience from portal data and syncs it up, which reaches
 * people who are not in ActiveCampaign yet but makes consent the sender's
 * question to answer.
 */
export type AudienceMode = 'ac_list' | 'portal' | 'both';

export interface AddedRecipient {
  email: string;
  firstName: string;
  lastName: string;
}

export interface AudienceFilter {
  mode: AudienceMode;
  /** Both may be set. The audience is their union, de-duplicated by email. */
  useAcList: boolean;
  usePortal: boolean;
  /** The ActiveCampaign list to include when useAcList is set. */
  acListId?: number;
  sources: AudienceSource[];
  clientTypes: ('client' | 'lead')[];
  industries: string[];
  states: string[];
  excludeConverted: boolean;
  search: string;
  excludedEmails: string[];
  addedRecipients: AddedRecipient[];
  /** SMS only: the ActiveCampaign SMS list a broadcast goes to. */
  smsListId?: number;
}

/** One contact on an ActiveCampaign list, as shown in the portal's list browser. */
export interface AcContact {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface AudienceMember {
  sourceType: AudienceSource;
  sourceId: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  smsEligible: boolean;
}

export type OptOutReason = 'unsubscribed' | 'bounced' | 'manual';

export const OPT_OUT_LABELS: Record<OptOutReason, string> = {
  unsubscribed: 'Unsubscribed',
  bounced: 'Address bounced',
  manual: 'Removed by staff',
};

export interface AudiencePreview {
  filter: AudienceFilter;
  total: number;
  bySource: Record<string, number>;
  sample: AudienceMember[];
  /** People who matched but were left out because they opted out. Email only. */
  leftOut?: { email: string; reason: OptOutReason }[];
  leftOutTotal?: number;
  /** Which side counted the audience: ActiveCampaign, or the portal's resolver. */
  source?: 'ac_list' | 'portal';
}

/** ActiveCampaign's reportTotals response, cached on the campaign row. */
export interface CampaignStats {
  status: number | null;
  sendAmt: number | null;
  totalAmt: number | null;
  uniqueOpens: number | null;
  verifiedUniqueOpens: number | null;
  subscriberClicks: number | null;
  uniqueLinkClicks: number | null;
  unsubscribes: number | null;
  totalBounces: number | null;
  uniqueReplies: number | null;
  forwards: number | null;
  socialShares: number | null;
  ldate: string | null;
}

export interface CampaignRow {
  id: number;
  name: string;
  channel: CampaignChannel;
  status: CampaignStatus;

  audience_filter: AudienceFilter;
  audience_count: number;

  subject: string;
  preheader: string;
  from_name: string;
  from_email: string;
  reply_to: string;
  template_key: string;
  template_data: Record<string, string>;
  sms_body: string;

  ac_list_id: number | null;
  ac_message_id: number | null;
  ac_campaign_id: number | null;
  ac_broadcast_id: number | null;

  scheduled_for: string | null;
  sent_at: string | null;
  /** Contacts synced so far; only meaningful while the status is 'sending'. */
  send_progress: number;
  last_error: string;

  stats: CampaignStats | null;
  stats_fetched_at: string | null;

  created_by_user_id: number | null;
  created_by_name: string | null;
  sent_by_user_id: number | null;
  sent_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'url';
  required: boolean;
  maxLength: number;
  help?: string;
}

export interface CampaignTemplate {
  key: string;
  name: string;
  description: string;
  fields: TemplateField[];
}

export interface CampaignOptions {
  templates: CampaignTemplate[];
  industries: string[];
  states: string[];
  emailLists: { id: number; name: string; contacts: number }[];
  smsLists: { id: number; name: string; contacts: number }[];
  smsEnabled: boolean;
  /** Set when a text cannot send: the account cannot, or it is out of credits. */
  smsBlocker: string | null;
  defaults: { fromName: string; fromEmail: string; replyTo: string };
}

/** Account-level setup state, so "why can't I send" is answerable on screen. */
export interface CampaignReadiness {
  configured: boolean;
  smsEnabled: boolean;
  addressId: number | null;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  blockers: string[];
  /** Whether unsubscribes and bounces are reaching the portal's opt-out record. */
  optOuts?: {
    webhookConfigured: boolean;
    webhookRegistered: boolean;
    lastWebhookAt: string | null;
    lastPullAt: string | null;
    lastPullAdded: number;
    lastPullError: string;
    suppressedCount: number;
  } | null;
  addresses?: { id: number; company: string }[];
  smsCredits?: {
    includedThisPeriod: number;
    usedThisPeriod: number;
    extra: number;
    available: number;
    nextRefillDate: string | null;
  } | null;
}

export interface EmailPreview {
  html: string;
  text: string;
  errors: string[];
}

export interface SmsPreview {
  sms: { body: string; characters: number; segments: number; encoding: 'GSM-7' | 'UCS-2' };
}

export function defaultAudienceFilter(): AudienceFilter {
  return {
    // The ActiveCampaign list is the default because it is the mode that cannot
    // introduce a consent problem, so it is the one to land on by accident.
    mode: 'ac_list',
    useAcList: true,
    usePortal: false,
    sources: ['client'],
    clientTypes: ['client', 'lead'],
    industries: [],
    states: [],
    excludeConverted: true,
    search: '',
    excludedEmails: [],
    addedRecipients: [],
  };
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

const qs = (userId: number) => `userId=${encodeURIComponent(userId)}`;

async function post<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<T>(res, fallback);
}

export async function fetchCampaignReadiness(userId: number): Promise<CampaignReadiness> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/readiness?${qs(userId)}`);
  const data = await jsonOrThrow<{ readiness: CampaignReadiness }>(
    res,
    'Failed to check the ActiveCampaign setup'
  );
  return data.readiness;
}

export async function fetchCampaignOptions(userId: number): Promise<CampaignOptions> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/options?${qs(userId)}`);
  return jsonOrThrow<CampaignOptions>(res, 'Failed to load campaign options');
}

export async function fetchCampaigns(userId: number): Promise<CampaignRow[]> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns?${qs(userId)}`);
  const data = await jsonOrThrow<{ campaigns: CampaignRow[] }>(res, 'Failed to load campaigns');
  return data.campaigns ?? [];
}

export async function fetchCampaign(id: number, userId: number): Promise<CampaignRow> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/${id}?${qs(userId)}`);
  const data = await jsonOrThrow<{ campaign: CampaignRow }>(res, 'Failed to load the campaign');
  return data.campaign;
}

export async function previewAudience(
  userId: number,
  filter: AudienceFilter,
  channel: CampaignChannel
): Promise<AudiencePreview> {
  return post<AudiencePreview>(
    '/api/marketing/campaigns/audience-preview',
    { user_id: userId, filter, channel },
    'Failed to resolve the audience'
  );
}

export async function createCampaign(
  userId: number,
  payload: { name: string; channel: CampaignChannel; filter?: AudienceFilter }
): Promise<CampaignRow> {
  const data = await post<{ campaign: CampaignRow }>(
    '/api/marketing/campaigns',
    { user_id: userId, ...payload },
    'Failed to create the campaign'
  );
  return data.campaign;
}

export interface CampaignUpdate {
  name?: string;
  subject?: string;
  preheader?: string;
  from_name?: string;
  from_email?: string;
  reply_to?: string;
  template_key?: string;
  template_data?: Record<string, string>;
  sms_body?: string;
  filter?: AudienceFilter;
  scheduled_for?: string | null;
}

export async function updateCampaign(
  id: number,
  userId: number,
  payload: CampaignUpdate
): Promise<CampaignRow> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, ...payload }),
  });
  const data = await jsonOrThrow<{ campaign: CampaignRow }>(res, 'Failed to save the campaign');
  return data.campaign;
}

export async function deleteCampaign(id: number, userId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/${id}?${qs(userId)}`, {
    method: 'DELETE',
  });
  await jsonOrThrow(res, 'Failed to delete the campaign');
}

export async function duplicateCampaign(id: number, userId: number): Promise<CampaignRow> {
  const data = await post<{ campaign: CampaignRow }>(
    `/api/marketing/campaigns/${id}/duplicate`,
    { user_id: userId },
    'Failed to duplicate the campaign'
  );
  return data.campaign;
}

/** Renders unsaved composer state, so the preview keeps up while writing. */
export async function previewCampaign(
  id: number,
  userId: number,
  overrides: Partial<CampaignUpdate> = {}
): Promise<EmailPreview & Partial<SmsPreview>> {
  return post<EmailPreview & Partial<SmsPreview>>(
    `/api/marketing/campaigns/${id}/preview`,
    { user_id: userId, ...overrides },
    'Failed to render the preview'
  );
}

export async function sendCampaignTest(
  id: number,
  userId: number,
  email: string
): Promise<{ sentTo: string }> {
  return post<{ sentTo: string }>(
    `/api/marketing/campaigns/${id}/test`,
    { user_id: userId, email },
    'Failed to send the test copy'
  );
}

/**
 * Starts the send and returns immediately with the campaign in 'sending'.
 *
 * The handoff to ActiveCampaign runs in the background because it makes one
 * request per contact; poll the campaign for `send_progress` and the final
 * status.
 */
export async function sendCampaign(id: number, userId: number): Promise<CampaignRow> {
  const data = await post<{ campaign: CampaignRow }>(
    `/api/marketing/campaigns/${id}/send`,
    { user_id: userId },
    'Failed to start the send'
  );
  return data.campaign;
}

export async function controlCampaign(
  id: number,
  userId: number,
  action: 'pause' | 'resume' | 'stop'
): Promise<CampaignRow> {
  const data = await post<{ campaign: CampaignRow }>(
    `/api/marketing/campaigns/${id}/${action}`,
    { user_id: userId },
    `Failed to ${action} the campaign`
  );
  return data.campaign;
}

export async function refreshCampaignStats(id: number, userId: number): Promise<CampaignRow> {
  const data = await post<{ campaign: CampaignRow }>(
    `/api/marketing/campaigns/${id}/refresh-stats`,
    { user_id: userId },
    'Failed to refresh the campaign report'
  );
  return data.campaign;
}

export interface CampaignRecipient {
  source_type: AudienceSource | 'activecampaign_contact';
  source_id: number;
  email: string;
  phone: string;
}

/**
 * Who a campaign went to.
 *
 * `live` is true for a campaign that targeted an ActiveCampaign list, where the
 * membership is read back from ActiveCampaign rather than from a snapshot — so
 * it reflects the list now, not necessarily at the moment of the send.
 */
export async function fetchCampaignRecipients(
  id: number,
  userId: number
): Promise<{ recipients: CampaignRecipient[]; total: number; live: boolean }> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/${id}/recipients?${qs(userId)}`);
  const data = await jsonOrThrow<{
    recipients: CampaignRecipient[];
    total: number;
    live: boolean;
  }>(res, 'Failed to load the recipient list');
  return { recipients: data.recipients ?? [], total: data.total ?? 0, live: !!data.live };
}

/** A page of the contacts on an ActiveCampaign list, for the list browser. */
export async function fetchAcListContacts(
  listId: number,
  userId: number,
  options: { limit?: number; offset?: number; search?: string } = {}
): Promise<{ contacts: AcContact[]; total: number }> {
  const params = new URLSearchParams({ userId: String(userId) });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.search) params.set('search', options.search);
  const res = await fetch(
    `${API_BASE}/api/marketing/campaigns/ac-lists/${listId}/contacts?${params}`
  );
  return jsonOrThrow<{ contacts: AcContact[]; total: number }>(
    res,
    'Failed to load the list contacts'
  );
}

// ---------------------------------------------------------------------------
// Email check
// ---------------------------------------------------------------------------

/** Where a checked address is stored. 'added' is someone typed into this campaign by hand. */
export type EmailRecordSource = AudienceSource | 'activecampaign' | 'added';

export const EMAIL_RECORD_LABELS: Record<EmailRecordSource, string> = {
  client: 'Client record',
  website_form_lead: 'Website lead',
  business_card_scan: 'Business card',
  event_intake_response: 'Event intake',
  robotics_proposal: 'Robotics proposal',
  activecampaign: 'ActiveCampaign contact',
  added: 'Added by hand',
};

export interface EmailIssueRecord {
  source: EmailRecordSource;
  sourceId: number;
  label: string;
}

export interface EmailIssue {
  email: string;
  /** 'invalid' will not deliver; 'typo' is probably mistyped but could be real. */
  kind: 'invalid' | 'typo';
  problem: string;
  suggestion: string | null;
  records: EmailIssueRecord[];
}

export interface EmailCheckResult {
  /** Distinct addresses checked. */
  checked: number;
  issues: EmailIssue[];
  /** Addresses whose domain could not be looked up in time, so only their spelling was checked. */
  unchecked: number;
  /** The ActiveCampaign list is longer than the portal reads, so only part of it was checked. */
  truncated: boolean;
}

export interface EmailFixResult {
  email: string;
  results: { source: EmailRecordSource; sourceId: number; ok: boolean; error?: string }[];
  /** The corrected address is on the opt-out list, so it will not be emailed. */
  optedOut: boolean;
}

/** The corrected address was not saved. `needsConfirm` means it only looks like a typo and can be saved anyway. */
export class EmailFixError extends Error {
  readonly suggestion: string | null;
  readonly needsConfirm: boolean;

  constructor(message: string, suggestion: string | null, needsConfirm: boolean) {
    super(message);
    this.name = 'EmailFixError';
    this.suggestion = suggestion;
    this.needsConfirm = needsConfirm;
  }
}

/** Checks every address the audience would reach, including ones too malformed to send to. */
export async function checkCampaignEmails(
  userId: number,
  filter: AudienceFilter
): Promise<EmailCheckResult> {
  return post<EmailCheckResult>(
    '/api/marketing/campaigns/email-check',
    { user_id: userId, filter },
    'Failed to check the email addresses'
  );
}

/** Saves a corrected address to the records it came from: portal rows and ActiveCampaign contacts. */
export async function fixCampaignEmail(
  userId: number,
  fix: { email: string; newEmail: string; records: EmailIssueRecord[]; confirmTypo?: boolean }
): Promise<EmailFixResult> {
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/email-check/fix`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      email: fix.email,
      newEmail: fix.newEmail,
      records: fix.records.map(({ source, sourceId }) => ({ source, sourceId })),
      confirmTypo: fix.confirmTypo === true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new EmailFixError(
      data.error || 'Failed to save the address',
      typeof data.suggestion === 'string' ? data.suggestion : null,
      data.needsConfirm === true
    );
  }
  return data as EmailFixResult;
}

// ---------------------------------------------------------------------------
// Contact list and opt-outs
// ---------------------------------------------------------------------------

export type ContactStatusFilter = 'all' | 'mailable' | 'opted_out';

export interface DirectoryContact {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  source: AudienceSource;
  sourceId: number;
  /** When the address first appeared in the portal, across every source. */
  addedAt: string | null;
  optOutReason: OptOutReason | null;
  optOutAt: string | null;
}

export interface ContactQuery {
  /** Inclusive, YYYY-MM-DD. */
  from?: string;
  to?: string;
  search?: string;
  status?: ContactStatusFilter;
  sources?: AudienceSource[];
  limit?: number;
  offset?: number;
}

export async function fetchContactDirectory(
  userId: number,
  query: ContactQuery = {}
): Promise<{ contacts: DirectoryContact[]; total: number }> {
  const params = new URLSearchParams({ userId: String(userId) });
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.search) params.set('search', query.search);
  if (query.status && query.status !== 'all') params.set('status', query.status);
  if (query.sources && query.sources.length > 0) params.set('sources', query.sources.join(','));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.offset) params.set('offset', String(query.offset));
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/contacts?${params}`);
  return jsonOrThrow<{ contacts: DirectoryContact[]; total: number }>(
    res,
    'Failed to load the contact list'
  );
}

export interface OptOutRow {
  email: string;
  reason: OptOutReason;
  source: 'webhook' | 'sync' | 'manual';
  detail: string;
  suppressed_at: string;
}

export type OptOutStatus = NonNullable<CampaignReadiness['optOuts']>;

export async function fetchOptOuts(
  userId: number,
  query: { search?: string; limit?: number; offset?: number } = {}
): Promise<{ suppressions: OptOutRow[]; total: number; status: OptOutStatus }> {
  const params = new URLSearchParams({ userId: String(userId) });
  if (query.search) params.set('search', query.search);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.offset) params.set('offset', String(query.offset));
  const res = await fetch(`${API_BASE}/api/marketing/campaigns/suppressions?${params}`);
  return jsonOrThrow<{ suppressions: OptOutRow[]; total: number; status: OptOutStatus }>(
    res,
    'Failed to load the opt-out list'
  );
}

export async function addOptOut(userId: number, email: string, detail = ''): Promise<void> {
  await post('/api/marketing/campaigns/suppressions', { user_id: userId, email, detail }, 'Failed to add the opt-out');
}

export async function releaseOptOut(userId: number, email: string): Promise<void> {
  await post(
    '/api/marketing/campaigns/suppressions/release',
    { user_id: userId, email },
    'Failed to release the opt-out'
  );
}

/** Reads ActiveCampaign's unsubscribed and bounced contacts now. */
export async function pullOptOutsNow(userId: number): Promise<{ scanned: number; added: number }> {
  return post(
    '/api/marketing/campaigns/suppressions/pull',
    { user_id: userId },
    'Failed to read the opt-outs from ActiveCampaign'
  );
}

/** Open rate and click rate, guarding the divide-by-zero before a send lands. */
export function campaignRates(stats: CampaignStats | null): {
  openRate: number | null;
  clickRate: number | null;
  bounceRate: number | null;
} {
  const sent = stats?.sendAmt ?? 0;
  if (!stats || sent <= 0) return { openRate: null, clickRate: null, bounceRate: null };
  const pct = (n: number | null) => (n == null ? null : Math.round((n / sent) * 1000) / 10);
  return {
    // Verified opens exclude bot-attributed ones, which inflate the raw number.
    openRate: pct(stats.verifiedUniqueOpens ?? stats.uniqueOpens),
    clickRate: pct(stats.subscriberClicks),
    bounceRate: pct(stats.totalBounces),
  };
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  sending: 'Sending',
  scheduled: 'Scheduled',
  sent: 'Sent',
  paused: 'Paused',
  stopped: 'Stopped',
  failed: 'Failed',
};
